import {
  type CreateCustomerInput,
  CUSTOMER_SUB_TYPE_OPTIONS,
  CUSTOMER_SUB_TYPES,
  CUSTOMER_TYPE_OPTIONS,
  CUSTOMER_TYPES,
  type Customer,
  INVOICE_CADENCE_OPTIONS,
  INVOICE_CADENCES,
  type PatchCustomerInput,
} from '@clockwise/clients/customers';
import { isResolvableCountryInput } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Receipt, Tags } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { COUNTRY_FIELD_ERROR, CountrySelectField } from '@/components/custom/country-select';
import { CustomerAutocomplete } from '@/components/custom/customer-autocomplete';
import { DepartmentAutocomplete } from '@/components/custom/department-autocomplete';
import { FormAccordionItem } from '@/components/custom/form-accordion-item';
import { UserAutocomplete } from '@/components/custom/user-autocomplete';
import { Accordion } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { serviceRolesApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

export const AddressFormSchema = z.object({
  street: z.string().max(200).optional().or(z.literal('')),
  city: z.string().max(120).optional().or(z.literal('')),
  state: z.string().max(120).optional().or(z.literal('')),
  postalCode: z.string().max(20).optional().or(z.literal('')),
  // Stored as an ISO-3166 alpha-2 code. The select only emits codes; the
  // refine catches legacy free-text values that don't resolve so they're
  // fixed explicitly rather than silently kept or cleared.
  country: z
    .string()
    .max(120)
    .optional()
    .or(z.literal(''))
    .refine(isResolvableCountryInput, COUNTRY_FIELD_ERROR),
});

export const CustomerFormSchema = z
  .object({
    qbId: z.string().min(1, 'Required').max(64),
    name: z.string().min(1, 'Required').max(200),
    legalName: z.string().max(200).optional().or(z.literal('')),
    phone: z.string().max(64).optional().or(z.literal('')),
    billingContactId: z.string().uuid().optional().or(z.literal('')),
    creditTerm: z.string().max(120).optional().or(z.literal('')),
    startDate: z.string().optional().or(z.literal('')),
    departmentId: z.string().uuid().optional().or(z.literal('')),
    defaultInvoiceType: z.string().max(120).optional().or(z.literal('')),
    corporateCalendar: z.string().max(120).optional().or(z.literal('')),
    taxSpoc: z.string().max(200).optional().or(z.literal('')),
    invoiceNotes: z.string().max(4000).optional().or(z.literal('')),
    // Classification dropdowns. '' = unset; the select emits codes otherwise.
    invoiceCadence: z.enum(INVOICE_CADENCES).or(z.literal('')).optional(),
    customerType: z.enum(CUSTOMER_TYPES).or(z.literal('')).optional(),
    customerSubType: z.enum(CUSTOMER_SUB_TYPES).or(z.literal('')).optional(),
    salesRepId: z.string().uuid().or(z.literal('')).optional(),
    // Parent company. '' = stands alone. Eligibility (active, not internal, not
    // already a subsidiary, not itself) is enforced server-side — it needs reads
    // this form can't do, and the API returns a field-level error on this path.
    parentCustomerId: z.string().uuid().or(z.literal('')).optional(),
    // Yes/No dropdown mapped to boolean | undefined on submit. '' = unset.
    achAuthorized: z.enum(['yes', 'no']).or(z.literal('')).optional(),
    // Late fee. Kept as strings in the form and converted on submit, so a
    // half-typed amount doesn't churn the payload on every keystroke. There is
    // no "enabled" checkbox: an amount IS the fee being on.
    lateFeeKind: z.enum(['percentage', 'fixed_amount']).or(z.literal('')).optional(),
    lateFeeAmount: z.string().optional(),
    consolidatedInvoicing: z.boolean().optional(),
    address: AddressFormSchema.optional(),
    billingAddress: AddressFormSchema.optional(),
    isInternal: z.boolean().optional(),
  })
  // The late fee is validated as a PAIR, on the object rather than the field,
  // because neither half means anything alone. Without this an unparseable
  // amount was silently dropped along with its kind: the save succeeded having
  // recorded no fee, while the select still showed the operator's choice.
  // G12 — the error belongs on the field they typed in.
  .superRefine((v, ctx) => {
    const raw = (v.lateFeeAmount ?? '').trim();
    if (!v.lateFeeKind) return;
    if (raw === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['lateFeeAmount'],
        message: 'Enter an amount, or set Late fee to None.',
      });
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['lateFeeAmount'],
        message: 'Enter a number, e.g. 1.5.',
      });
      return;
    }
    if (v.lateFeeKind === 'percentage' && parsed > 100) {
      ctx.addIssue({
        code: 'custom',
        path: ['lateFeeAmount'],
        message: 'A percentage late fee cannot exceed 100.',
      });
    }
  });

export type CustomerFormValues = z.infer<typeof CustomerFormSchema>;

/**
 * The late-fee amount as a number, or null when the cell is empty.
 *
 * The empty case is tested BEFORE parsing: `Number('')` is 0, which would
 * record a real fee of zero for a field nobody filled in. An unparseable
 * value cannot reach this — the form schema rejects it on the field — so a
 * non-finite result here means the caller skipped validation.
 */
export function parseLateFeeAmount(raw: string | undefined): number | null {
  const trimmed = (raw ?? '').trim();
  return trimmed === '' ? null : Number(trimmed);
}

export const PRODUCT_FORM_FIELD_NAMES = [
  'qbId',
  'name',
  'legalName',
  'phone',
  'billingContactId',
  'creditTerm',
  'startDate',
  'departmentId',
  'defaultInvoiceType',
  'corporateCalendar',
  'taxSpoc',
  'invoiceNotes',
  'invoiceCadence',
  'customerType',
  'customerSubType',
  'salesRepId',
  'parentCustomerId',
  'achAuthorized',
  'lateFeeKind',
  'lateFeeAmount',
  'consolidatedInvoicing',
  'isInternal',
  'address',
  'billingAddress',
] as const;

export function isCustomerFormField(field: string): field is keyof CustomerFormValues {
  return (PRODUCT_FORM_FIELD_NAMES as readonly string[]).includes(field.split('.')[0] ?? field);
}

export const blankCustomerValues: CustomerFormValues = {
  qbId: '',
  name: '',
  legalName: '',
  phone: '',
  billingContactId: '',
  creditTerm: '',
  startDate: '',
  departmentId: '',
  defaultInvoiceType: '',
  corporateCalendar: '',
  taxSpoc: '',
  invoiceNotes: '',
  invoiceCadence: '',
  customerType: '',
  customerSubType: '',
  salesRepId: '',
  parentCustomerId: '',
  achAuthorized: '',
  lateFeeKind: '',
  lateFeeAmount: '',
  consolidatedInvoicing: false,
  isInternal: false,
  address: { street: '', city: '', state: '', postalCode: '', country: '' },
  billingAddress: { street: '', city: '', state: '', postalCode: '', country: '' },
};

interface Props {
  form: UseFormReturn<CustomerFormValues>;
  /** The customer's CURRENT parent, for the picker's label. Read-only
   *  reference data, so it rides beside the form values rather than in them.
   *
   *  Load-bearing under `customer.view: assigned`: the picker resolves its
   *  value through `GET /api/customers/:id`, which 403s for a parent the
   *  caller isn't assigned to. Without this the picker renders empty and
   *  saving writes that emptiness back, detaching a parent the user never
   *  touched and could not see. Pass it wherever a customer is being edited. */
  parentCustomer?: Customer | null;
}

const SERVICE_ROLES_LIST_QUERY = {
  sortField: 'shortCode',
  sortDirection: 'asc',
  limit: 200,
} as const;

export function CustomerFormFields({ form, parentCustomer }: Props) {
  const errors = form.formState.errors;
  const isCustomerAdmin = useHasPermission('customer.admin');

  // Sales Rep candidates are the holders of the SALES service role. Resolve
  // its id here so the (reused) UserAutocomplete can scope its fetch to it.
  const serviceRoles = useQuery({
    queryKey: queryKeys.serviceRoles.list(SERVICE_ROLES_LIST_QUERY),
    queryFn: () => serviceRolesApi.list(SERVICE_ROLES_LIST_QUERY),
  });
  const salesRoleId = (serviceRoles.data?.data ?? []).find(
    (r) => r.active && r.shortCode.toUpperCase() === 'SALES',
  )?.id;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id="name"
          label="Name"
          placeholder="Acme Corp"
          error={errors.name?.message}
          register={form.register('name')}
        />
        <Field
          id="legalName"
          label="Legal name"
          placeholder="Acme Corporation, Inc."
          error={errors.legalName?.message}
          register={form.register('legalName')}
        />
      </div>
      <Field
        id="qbId"
        label="QuickBooks ID"
        placeholder="QB-1234"
        hint="Required. Unique per tenant."
        error={errors.qbId?.message}
        register={form.register('qbId')}
      />
      <Field
        id="phone"
        label="Primary phone"
        placeholder="+1 555 123 4567"
        error={errors.phone?.message}
        register={form.register('phone')}
      />

      <div className="space-y-1.5">
        <Label htmlFor="departmentId">Department</Label>
        <DepartmentAutocomplete
          value={form.watch('departmentId') || null}
          onChange={(d) =>
            form.setValue('departmentId', d?.id ?? '', {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
        />
        {errors.departmentId?.message ? (
          <p className="text-xs text-destructive">{errors.departmentId.message}</p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="parentCustomerId">Parent customer</Label>
        <CustomerAutocomplete
          id="parentCustomerId"
          value={form.watch('parentCustomerId') || null}
          // Falls back to the customer's own record when the picker can't
          // fetch the parent (assigned scope) — see the prop's docs.
          selectedItem={parentCustomer ?? null}
          placeholder="None — stands alone"
          onChange={(c) =>
            form.setValue('parentCustomerId', c?.id ?? '', {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
        />
        {errors.parentCustomerId?.message ? (
          <p className="text-xs text-destructive">{errors.parentCustomerId.message}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            For subsidiary customers. A parent cannot itself be a subsidiary.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="startDate">Engagement Date</Label>
          <DatePicker
            id="startDate"
            value={form.watch('startDate') ?? ''}
            onChange={(v) =>
              form.setValue('startDate', v, { shouldValidate: true, shouldDirty: true })
            }
            placeholder="Pick a date"
            clearable
          />
          {errors.startDate?.message ? (
            <p className="text-xs text-destructive">{errors.startDate.message}</p>
          ) : null}
        </div>
        <Field
          id="creditTerm"
          label="Credit term"
          placeholder="Net 30"
          error={errors.creditTerm?.message}
          register={form.register('creditTerm')}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id="defaultInvoiceType"
          label="Default invoice type"
          placeholder="Standard"
          error={errors.defaultInvoiceType?.message}
          register={form.register('defaultInvoiceType')}
        />
        <Field
          id="corporateCalendar"
          label="Corporate calendar"
          placeholder="Calendar year"
          error={errors.corporateCalendar?.message}
          register={form.register('corporateCalendar')}
        />
      </div>
      <Field
        id="taxSpoc"
        label="Tax SPOC"
        placeholder="Single point of contact for tax"
        error={errors.taxSpoc?.message}
        register={form.register('taxSpoc')}
      />

      <FieldTextarea
        id="invoiceNotes"
        label="Invoice notes"
        placeholder="Default note appended to every invoice"
        error={errors.invoiceNotes?.message}
        register={form.register('invoiceNotes')}
      />

      {/* Same open-by-default accordion as the employee form, so the two
          record forms group their fields the same way. */}
      <Accordion type="multiple" defaultValue={['classification', 'address', 'billing-address']}>
        <FormAccordionItem
          value="classification"
          icon={Tags}
          title="Classification"
          description="How this customer is typed, invoiced, and who sells to it."
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SelectField
              id="customerType"
              label="Customer type"
              placeholder="Select type…"
              options={CUSTOMER_TYPE_OPTIONS}
              value={form.watch('customerType') ?? ''}
              onChange={(v) =>
                form.setValue('customerType', v as CustomerFormValues['customerType'], {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              error={errors.customerType?.message}
            />
            <SelectField
              id="customerSubType"
              label="Customer sub-type"
              placeholder="Select sub-type…"
              options={CUSTOMER_SUB_TYPE_OPTIONS}
              value={form.watch('customerSubType') ?? ''}
              onChange={(v) =>
                form.setValue('customerSubType', v as CustomerFormValues['customerSubType'], {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              error={errors.customerSubType?.message}
            />
            <SelectField
              id="invoiceCadence"
              label="Invoice cadence"
              placeholder="Select cadence…"
              options={INVOICE_CADENCE_OPTIONS}
              value={form.watch('invoiceCadence') ?? ''}
              onChange={(v) =>
                form.setValue('invoiceCadence', v as CustomerFormValues['invoiceCadence'], {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              error={errors.invoiceCadence?.message}
            />
            <SelectField
              id="achAuthorized"
              label="ACH authorization"
              placeholder="Select…"
              options={ACH_OPTIONS}
              value={form.watch('achAuthorized') ?? ''}
              onChange={(v) =>
                form.setValue('achAuthorized', v as CustomerFormValues['achAuthorized'], {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              error={errors.achAuthorized?.message}
            />
            <SelectField
              id="consolidatedInvoicing"
              label="Consolidated invoicing"
              placeholder="Select…"
              options={CONSOLIDATED_OPTIONS}
              value={form.watch('consolidatedInvoicing') ? 'yes' : 'no'}
              onChange={(v) =>
                form.setValue('consolidatedInvoicing', v === 'yes', {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
            <SelectField
              id="lateFeeKind"
              label="Late fee"
              placeholder="None"
              options={LATE_FEE_OPTIONS}
              value={form.watch('lateFeeKind') ?? ''}
              onChange={(v) => {
                form.setValue('lateFeeKind', v as CustomerFormValues['lateFeeKind'], {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                // Clearing the kind clears the amount: half a fee is not a
                // setting, and the server refuses it anyway.
                if (!v) form.setValue('lateFeeAmount', '', { shouldDirty: true });
              }}
              error={errors.lateFeeKind?.message}
            />
            {form.watch('lateFeeKind') ? (
              <div className="space-y-1.5">
                <Label htmlFor="lateFeeAmount">
                  Late fee amount {form.watch('lateFeeKind') === 'percentage' ? '(%)' : '($)'}
                </Label>
                {/* `type="text"` with inputMode, never `type="number"`: a number
                 * input edits its value on scroll, so passing over the form
                 * with the wheel would silently change a fee. */}
                <Input
                  id="lateFeeAmount"
                  type="text"
                  inputMode="decimal"
                  value={form.watch('lateFeeAmount') ?? ''}
                  onChange={(e) =>
                    form.setValue('lateFeeAmount', e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                  aria-invalid={Boolean(errors.lateFeeAmount)}
                />
                {errors.lateFeeAmount ? (
                  <p className="text-xs text-destructive" role="alert">
                    {errors.lateFeeAmount.message}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="salesRepId">Sales rep</Label>
            {salesRoleId ? (
              <UserAutocomplete
                serviceRoleId={salesRoleId}
                value={form.watch('salesRepId') || null}
                onChange={(u) =>
                  form.setValue('salesRepId', u?.id ?? '', {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                placeholder="Select sales rep…"
                clearable
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                {serviceRoles.isLoading
                  ? 'Loading…'
                  : 'No “SALES” service role is configured, so there are no sales reps to choose from.'}
              </p>
            )}
            {errors.salesRepId?.message ? (
              <p className="text-xs text-destructive">{errors.salesRepId.message}</p>
            ) : null}
          </div>
        </FormAccordionItem>

        <FormAccordionItem
          value="address"
          icon={MapPin}
          title="Physical address"
          description="Where the customer is located."
        >
          <AddressBlock pathPrefix="address" form={form} />
        </FormAccordionItem>
        <FormAccordionItem
          value="billing-address"
          icon={Receipt}
          title="Billing address"
          description="Printed on invoices as the bill-to address."
        >
          <AddressBlock pathPrefix="billingAddress" form={form} />
        </FormAccordionItem>
      </Accordion>

      {/* Admin-only structural setting; shown at the bottom and only to users
          with customer.admin. The server enforces the same gate. */}
      {isCustomerAdmin ? (
        <div className="space-y-1.5">
          <div className="flex items-start gap-2">
            <Checkbox
              id="isInternal"
              checked={form.watch('isInternal') ?? false}
              onCheckedChange={(c) =>
                form.setValue('isInternal', c === true, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              aria-invalid={Boolean(errors.isInternal)}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="isInternal">Internal customer</Label>
              <p className="text-xs text-muted-foreground">
                The employer itself. Only one customer per tenant can be internal. Employees are
                added to its team at a zero rate, and timesheets logged against it only offer
                internal activities.
              </p>
            </div>
          </div>
          {errors.isInternal?.message ? (
            <p className="text-xs text-destructive">{errors.isInternal.message}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const CONSOLIDATED_OPTIONS = [
  { value: 'yes', label: 'One invoice per period' },
  { value: 'no', label: 'One invoice per engagement' },
];

const LATE_FEE_OPTIONS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed_amount', label: 'Fixed amount' },
];

const ACH_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;

interface SelectFieldProps {
  id: string;
  label: string;
  placeholder: string;
  options: readonly { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

/** Labeled single-select for an optional enum. A "Clear" affordance resets to
 *  unset (Radix Select can't hold an empty-string item value). */
function SelectField({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  error,
}: SelectFieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-5 px-1 text-xs text-muted-foreground"
            onClick={() => onChange('')}
          >
            Clear
          </Button>
        ) : null}
      </div>
      {/* `key` remounts on a hydrated value so an edit-mode reset() lands on the
          right label — see PrimaryRoleField in product-form-fields for why. */}
      <Select key={value || 'unset'} value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function AddressBlock({
  pathPrefix,
  form,
}: {
  pathPrefix: 'address' | 'billingAddress';
  form: UseFormReturn<CustomerFormValues>;
}) {
  return (
    <div className="space-y-3">
      <Field
        id={`${pathPrefix}.street`}
        label="Street"
        register={form.register(`${pathPrefix}.street` as const)}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id={`${pathPrefix}.city`}
          label="City"
          register={form.register(`${pathPrefix}.city` as const)}
        />
        <Field
          id={`${pathPrefix}.state`}
          label="State / Region"
          register={form.register(`${pathPrefix}.state` as const)}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id={`${pathPrefix}.postalCode`}
          label="Postal code"
          register={form.register(`${pathPrefix}.postalCode` as const)}
        />
        <CountrySelectField
          form={form}
          name={`${pathPrefix}.country` as const}
          id={`${pathPrefix}.country`}
        />
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  register: ReturnType<UseFormReturn<CustomerFormValues>['register']>;
}

function Field({ id, label, placeholder, hint, error, register }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} placeholder={placeholder} aria-invalid={Boolean(error)} {...register} />
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function FieldTextarea({ id, label, placeholder, error, register }: Omit<FieldProps, 'hint'>) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        rows={3}
        {...register}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/** Collapse empty strings → undefined so a never-touched field is "leave alone". */
export function blankToUndef(v: string | undefined | null): string | undefined {
  return v == null || v === '' ? undefined : v;
}

/**
 * The other PATCH semantic: '' → `null` = CLEAR the field. Use this instead of
 * `blankToUndef` wherever emptying the control is a real user action the server
 * must act on; `blankToUndef` would collapse it to `undefined` ("leave alone")
 * and the save would appear to succeed while changing nothing.
 *
 * `parentCustomerId` is the motivating case — detaching a subsidiary from its
 * parent IS "set this to empty" — and it's the same reasoning that already
 * makes `classificationPayload` below emit null rather than undefined.
 */
export function blankToNull(v: string | undefined | null): string | null {
  return v == null || v === '' ? null : v;
}

/**
 * Build the parent-picker's fallback label out of the child's OWN record.
 *
 * The customer detail response projects `parentName` server-side precisely so
 * the UI never has to fetch the parent through the view-scoped customer
 * endpoint — under `customer.view: assigned` that request 403s for a parent the
 * caller isn't on the team of, the picker renders blank, and saving the form
 * writes the blank back and detaches the parent. See `CustomerAutocomplete`'s
 * `selectedItem` docs.
 *
 * Only `id` and `name` are ever read from it (the picker's `getId`/`getLabel`),
 * hence the cast: the rest of `Customer` is data we deliberately do not have.
 * Returns null when there's nothing to stand in for — no parent, or a parent
 * whose name didn't resolve — leaving the picker's own fetch to try.
 */
export function parentCustomerStandIn(
  customer: { parentCustomerId: string | null; parentName?: string | null } | null | undefined,
): Customer | null {
  if (!customer?.parentCustomerId || !customer.parentName) return null;
  return { id: customer.parentCustomerId, name: customer.parentName } as Customer;
}

/** The classification subset of a create/patch payload, mapped from form
 *  values: '' → null, ACH yes/no → boolean. `null` (not `undefined`) so that
 *  clearing a dropdown on the edit form actually unsets the field — PATCH
 *  treats `undefined` as "leave unchanged", which would make the Clear button
 *  a no-op. On create, null is equivalent to omitting (the column is nullable).
 *  Shared by the add sheet and the edit route so the two never diverge. */
export function classificationPayload(
  values: CustomerFormValues,
): Pick<
  CreateCustomerInput,
  | 'invoiceCadence'
  | 'customerType'
  | 'customerSubType'
  | 'salesRepId'
  | 'achAuthorized'
  | 'lateFeeKind'
  | 'lateFeeAmount'
  | 'consolidatedInvoicing'
> {
  // A BLANK amount means "no late fee" and sends the pair as null. An
  // unparseable one never reaches here — the form schema rejects it on the
  // field — so this only has to handle the empty case.
  const parsed = parseLateFeeAmount(values.lateFeeAmount);
  const lateFeeAmount = parsed != null && Number.isFinite(parsed) ? parsed : null;
  const lateFeeKind = lateFeeAmount == null ? null : values.lateFeeKind || null;
  return {
    invoiceCadence: values.invoiceCadence || null,
    customerType: values.customerType || null,
    customerSubType: values.customerSubType || null,
    salesRepId: values.salesRepId || null,
    achAuthorized:
      values.achAuthorized === 'yes' ? true : values.achAuthorized === 'no' ? false : null,
    // Both halves move together, so a kind with no usable amount sends neither.
    lateFeeKind: lateFeeKind === null ? null : lateFeeKind,
    lateFeeAmount: lateFeeKind === null ? null : lateFeeAmount,
    consolidatedInvoicing: values.consolidatedInvoicing ?? false,
  };
}

/** Inverse of {@link classificationPayload}: a stored customer → the form's
 *  classification field values, for `form.reset()` in edit mode. */
export function classificationToForm(
  customer: Customer,
): Pick<
  CustomerFormValues,
  | 'invoiceCadence'
  | 'customerType'
  | 'customerSubType'
  | 'salesRepId'
  | 'achAuthorized'
  | 'lateFeeKind'
  | 'lateFeeAmount'
  | 'consolidatedInvoicing'
> {
  return {
    invoiceCadence: customer.invoiceCadence ?? '',
    customerType: customer.customerType ?? '',
    customerSubType: customer.customerSubType ?? '',
    salesRepId: customer.salesRepId ?? '',
    achAuthorized: customer.achAuthorized == null ? '' : customer.achAuthorized ? 'yes' : 'no',
    lateFeeKind: customer.lateFeeKind ?? '',
    lateFeeAmount: customer.lateFeeAmount ?? '',
    consolidatedInvoicing: customer.consolidatedInvoicing,
  };
}

/** Address object with all-blank fields → undefined; otherwise return the trimmed object. */
export function normalizeAddress(
  v: CustomerFormValues['address'] | undefined,
):
  | { street?: string; city?: string; state?: string; postalCode?: string; country?: string }
  | undefined {
  if (!v) return undefined;
  const trimmed = {
    street: blankToUndef(v.street),
    city: blankToUndef(v.city),
    state: blankToUndef(v.state),
    postalCode: blankToUndef(v.postalCode),
    country: blankToUndef(v.country),
  };
  const hasAny = Object.values(trimmed).some((x) => x !== undefined);
  return hasAny ? trimmed : undefined;
}

/**
 * A customer record → the form's values.
 *
 * Extracted from the edit route so the historical-amend dialog seeds its form
 * the same way. Two callers, one mapping: a field added to the schema and
 * wired in only one of them is a field that silently stops round-tripping.
 *
 * Everything nullable becomes `''` rather than staying null, because the
 * inputs are controlled and a null value flips them to uncontrolled mid-life.
 */
export function customerToFormValues(customer: Customer): CustomerFormValues {
  return {
    qbId: customer.qbId,
    name: customer.name,
    legalName: customer.legalName ?? '',
    phone: customer.phone ?? '',
    billingContactId: customer.billingContactId ?? '',
    creditTerm: customer.creditTerm ?? '',
    startDate: customer.startDate ?? '',
    departmentId: customer.departmentId ?? '',
    parentCustomerId: customer.parentCustomerId ?? '',
    defaultInvoiceType: customer.defaultInvoiceType ?? '',
    corporateCalendar: customer.corporateCalendar ?? '',
    taxSpoc: customer.taxSpoc ?? '',
    invoiceNotes: customer.invoiceNotes ?? '',
    ...classificationToForm(customer),
    isInternal: customer.isInternal,
    address: {
      street: customer.address?.street ?? '',
      city: customer.address?.city ?? '',
      state: customer.address?.state ?? '',
      postalCode: customer.address?.postalCode ?? '',
      country: customer.address?.country ?? '',
    },
    billingAddress: {
      street: customer.billingAddress?.street ?? '',
      city: customer.billingAddress?.city ?? '',
      state: customer.billingAddress?.state ?? '',
      postalCode: customer.billingAddress?.postalCode ?? '',
      country: customer.billingAddress?.country ?? '',
    },
  } as CustomerFormValues;
}

/**
 * The form's values → a patch body. Counterpart to
 * {@link customerToFormValues}, and shared for the same reason.
 *
 * `customFields` is passed in rather than read here: the editor that owns them
 * is a sibling of the form, not part of it.
 */
export function customerFormToPatch(
  values: CustomerFormValues,
  customFields: Record<string, unknown>,
): PatchCustomerInput {
  return {
    name: values.name,
    legalName: blankToUndef(values.legalName),
    qbId: blankToUndef(values.qbId),
    phone: blankToUndef(values.phone),
    billingContactId: blankToUndef(values.billingContactId),
    creditTerm: blankToUndef(values.creditTerm),
    startDate: blankToUndef(values.startDate),
    departmentId: blankToUndef(values.departmentId),
    defaultInvoiceType: blankToUndef(values.defaultInvoiceType),
    corporateCalendar: blankToUndef(values.corporateCalendar),
    taxSpoc: blankToUndef(values.taxSpoc),
    invoiceNotes: blankToUndef(values.invoiceNotes),
    ...classificationPayload(values),
    // blankToNull, NOT blankToUndef: emptying the picker means "detach from the
    // parent", which has to reach the API as an explicit null. Collapsed to
    // undefined it would read as "leave unchanged" and the save would appear to
    // succeed while changing nothing.
    parentCustomerId: blankToNull(values.parentCustomerId),
    isInternal: values.isInternal ?? false,
    address: normalizeAddress(values.address),
    billingAddress: normalizeAddress(values.billingAddress),
    customFields: customFields as PatchCustomerInput['customFields'],
  };
}
